import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Big from 'big.js';
import { publishSuccessNotification, publishErrorNotification } from '@nbx/frontend-helpers/publish';
import { NBXAccounts } from 'services';
import Field from 'components/shared/Field';
import ButtonPrimary from 'components/shared/ButtonPrimary';
import Text from 'components/shared/Text';
import { truncate } from '@nbx/frontend-helpers/format';
import { replaceDecimalSeparator } from 'helpers/strings';
import { isNullish } from 'helpers/objects';
import SUCCESS from 'constants/success';
import ERRORS from 'constants/errors';
import ModalSlippageWarning from './ModalSlippageWarning';
import OrderSummary from './OrderSummary';
import { parseFloatValue, getEstimation, getBalance } from './helpers';
import {
  validateBaseSizeMinimum,
  validateNumeric,
  validateAmountPrecision,
  validateInsufficientFundsMarket,
  validateCanFillOrder,
  validateQuoteTotalPrecision
} from './validation';
import styles from './styles.module.scss';
import { computeOrderbookMidPrice, computeSlippage } from './slippage';
import { QtyPercentageButtons } from './QtyPercentageButtons';

Big.RM = 0;
const microapps = window.MICROFRONTENDS_CONFIG;

function OrderFormMarket(props) {
  function getOrders(side, orderBook) {
    return side === 'sell' ? orderBook.data.buys : [...orderBook.data.sells].reverse();
  }

  // State & Initial State Vars

  const { t } = useTranslation();
  const initialData = {
    freeze: null,
    pair: props.pair,
    price: props.trades.data.marketPrice,
    quantity: 0,
    side: props.side.toUpperCase(),
    tif: 'IMMEDIATE_OR_CANCEL',
    category: 'MARKET'
  };

  const initialValidQuantity = { valid: false, error: null };

  const [orderData, setOrderData] = useState(initialData);
  const [validQuantity, setValidQuantity] = useState(initialValidQuantity);
  const [showSlippageWarning, setShowSlippageWarning] = useState(false);
  const [isFocused, setFocused] = useState(false);
  const [submittingOrder, setSubmittingOrder] = useState(false);

  const computeEstimation = quantity => {
    const orders = getOrders(props.side, props.orderBook);
    return getEstimation(parseFloatValue(quantity), props.takerFee, orders);
  };

  useEffect(
    () => {
      setOrderData(initialData);
      setValidQuantity(initialValidQuantity);
    },
    [props.pair, props.side] // eslint-disable-line
  );

  // Handling order book row clicks
  const { qty: selectedQty } = props.selectedOrder;
  const prevSelectedQty = useRef(selectedQty);

  useEffect(
    () => {
      if (selectedQty === null) {
        if (prevSelectedQty.current !== null) prevSelectedQty.current = null;
        return;
      }
      if (selectedQty !== prevSelectedQty.current) {
        prevSelectedQty.current = selectedQty;
        let quantity = selectedQty;
        if (props.side === 'buy') {
          const currentBalance = Big(getBalance(props.funds.balances, props.market.quoteCurrency));
          if (!isNullish(props.trades.data.marketPrice)) {
            const total = Big(props.trades.data.marketPrice).times(quantity);
            if (total.gt(currentBalance)) quantity = currentBalance.div(props.trades.data.marketPrice);
          }
        } else {
          const currentBalance = getBalance(props.funds.balances, props.market.baseCurrency);
          if (Big(quantity).gt(currentBalance)) quantity = currentBalance;
        }
        if (Big(quantity).gt(0)) {
          quantity = truncate(quantity, props.market.minQtyLength);
          validateOrderQuantity(quantity);
          setOrderData({ ...orderData, quantity });
        }
      }
    },
    [orderData, selectedQty] // eslint-disable-line
  );

  // Form-Specific Validation
  const validateOrderQuantity = value => {
    // check is numeric
    const validNumeric = validateNumeric(value);
    if (!validNumeric) {
      setValidQuantity({ valid: false, error: t('market_err_quantity_numeric') });
      return;
    }

    // check is above min order quantity for market
    const isAboveBaseSizeMin = validateBaseSizeMinimum(value, props.market);
    if (!isAboveBaseSizeMin) {
      setValidQuantity({
        valid: false,
        error: t('market_err_must_be_greater', {
          minQty: ` ${props.market.baseSizeMinimum} ${props.market.baseCurrency}`
        })
      });
      return;
    }

    const estimation = computeEstimation(replaceDecimalSeparator(value));
    if (!validateQuoteTotalPrecision(estimation.total, props.market)) {
      setValidQuantity({
        valid: false,
        error: t('market_err_total_atleast', {
          minTotal: `${props.market.quoteIncrement} ${props.market.quoteAsset}`
        })
      });
      return;
    }

    if (props.userId !== null) {
      const orders = getOrders(props.side, props.orderBook);
      const canFillOrder = validateCanFillOrder(parseFloatValue(value), orders);
      if (!canFillOrder) {
        setValidQuantity({ valid: false, error: t('error_amount_is_larger_than_market') });
        return;
      }

      const { insufficientFunds, asset } = validateInsufficientFundsMarket(
        value,
        props.side,
        props.market,
        props.funds.balances,
        props.trades.data.marketPrice
      );

      if (insufficientFunds) {
        setValidQuantity({ valid: false, error: t('market_err_insuff_funds', { asset }) });
        return;
      }
    }

    // if no error, reset
    setValidQuantity({ valid: true, error: null });
  };

  // Slippage Warning Validation
  const validateExcessiveSlippage = quantity => {
    const orderQuantity = Big(quantity);
    const orderBookOrders = getOrders(props.side, props.orderBook);
    const orderBookMidPrice = computeOrderbookMidPrice(props.orderBook.data);
    const { slippageExceeded } = computeSlippage(orderBookOrders, orderQuantity, orderBookMidPrice);
    return !slippageExceeded;
  };

  // Event Handlers
  const handleChange = e => {
    const { value } = e.target;
    let correctedValue = value;
    if (!value) {
      setValidQuantity(initialValidQuantity);
    } else {
      validateOrderQuantity(value);
      if (!validateAmountPrecision(value, props.market.minQtyLength)) {
        correctedValue = truncate(value, props.market.minQtyLength);
      }
    }
    setOrderData({
      ...orderData,
      quantity: correctedValue
    });
  };

  const handleFocus = () => {
    setFocused(!isFocused);

    if (!orderData.quantity) {
      setOrderData({
        ...orderData,
        quantity: !isFocused ? '' : 0
      });
    }
  };

  const changeQuantity = (percentage = 1) => {
    let newQuantity;
    if (props.side === 'buy') {
      const balance = Big(getBalance(props.funds.balances, props.market.quoteCurrency));
      if (balance.eq(0)) newQuantity = Big(0);
      else {
        // qty is adjusted so that total + fee is <= user's balance in quoteAsset
        // total you can afford = balance / 1+takerFee + ((balance / 1+takerFee) * takerFee)
        const adjustedBalance = balance.mul(Big(percentage)).div(Big(props.takerFee).div(100).plus(1));
        newQuantity = adjustedBalance.div(props.trades.data.marketPrice || 1);
      }
    } else {
      const balance = Big(getBalance(props.funds.balances, props.market.baseCurrency));
      newQuantity = balance.mul(Big(percentage));
    }
    if (isNaN(newQuantity) || newQuantity.eq(0)) return;

    newQuantity = truncate(newQuantity, props.market.minQtyLength);
    handleChange({ target: { id: 'quantity', value: `${newQuantity}` } });
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!validQuantity.valid) return;
    const correctedQty = replaceDecimalSeparator(orderData.quantity);
    if (!validateExcessiveSlippage(correctedQty)) {
      setShowSlippageWarning(true);
      return;
    }
    postOrder();
  };

  const ignoreSlippageWarning = () => {
    setShowSlippageWarning(false);
    postOrder();
  };

  const postOrder = () => {
    setSubmittingOrder(true);
    const correctedQty = replaceDecimalSeparator(orderData.quantity);
    NBXAccounts.createOrder({
      ...orderData,
      freeze:
        props.side === 'buy'
          ? {
              type: 'amount',
              value: getBalance(props.funds.balances, props.market.quoteCurrency) // Limit always needs to the quote asset, see issue #1045
            }
          : null,
      price: null,
      quantity: correctedQty
    })
      .then(id => {
        setOrderData(initialData);
        setValidQuantity(initialValidQuantity);
        publishSuccessNotification({ message: SUCCESS.orderCreate });
        props.onSuccess({
          id,
          qty: correctedQty,
          total: Big(correctedQty).mul(Big(orderData.price)),
          pair: orderData.pair,
          side: orderData.side
        });
      })
      .catch(error => publishErrorNotification({ message: ERRORS.orderCreate }, error))
      .finally(() => {
        setSubmittingOrder(false);
      });
  };

  const estimation = computeEstimation(orderData.quantity);

  const summaryItems = [
    {
      title: t('common_fee'),
      value: estimation.fee,
      minDisplayValue: 0,
      minTickLength: props.market.minTickLength,
      muted: true,
      symbol: props.market.quoteCurrency
    },
    {
      title: t('common_total'),
      value: estimation.total,
      minDisplayValue: 0,
      minTickLength: props.market.minTickLength,
      muted: true,
      showLessThan: true,
      symbol: props.market.quoteCurrency
    }
  ];

  return (
    <>
      <form className={styles.form} autoComplete="off" onSubmit={handleSubmit}>
        <Field
          id="price"
          name={t('common_price')}
          noLabel
          inputMode="decimal"
          symbol={props.market.quoteCurrency}
          placeholder={t('common_market_price')}
          onChange={() => {}}
          readOnly
          fieldWrapperStyles={[styles.priceFieldWrapper]}
          trailingInlineElements={
            <div className={styles.inlineContainer}>
              <Text muted styles={[styles.inlineReadOnly]}>
                {props.market.quoteCurrency}
              </Text>
            </div>
          }
        />
        <Field
          id="quantity"
          name={t('common_amount')}
          inputMode="decimal"
          error={validQuantity.error}
          symbol={props.market.baseCurrency}
          inlineLabel
          inlineLabelMinWidth="68px"
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleFocus}
          placeholder={0}
          value={orderData.quantity}
          fieldWrapperStyles={[styles.quantityFieldWrapper]}
          trailingInlineElements={
            <div className={styles.inlineContainer}>
              <Text muted>{props.market.baseCurrency}</Text>
            </div>
          }
        />

        <QtyPercentageButtons onChangeQuantity={changeQuantity} />

        <OrderSummary summaryItems={summaryItems} symbol={props.market.quoteCurrency} size="12px" />

        <div className={styles.buttonActionWrapper}>
          <ButtonPrimary
            type={!props.userId ? 'button' : 'submit'}
            disabled={!!props.userId && (submittingOrder || !validQuantity.valid)}
            to={props.userId === null ? microapps?.routes?.signup?.path : undefined}
            loading={submittingOrder}
            styles={[
              styles.buttonAction,
              {
                [styles.buttonActionBuy]: props.side === 'buy',
                [styles.buttonActionSell]: props.side === 'sell',
                [styles.buttonActionNoUser]: props.userId === null
              }
            ]}>
            {
              props.userId !== null
                ? `${props.side === 'buy' ? t('common_buy') : t('common_sell')} ${props.market.baseCurrency}`
                : t('market_signup_to_trade') // Link?
            }
          </ButtonPrimary>
        </div>
      </form>
      {showSlippageWarning && (
        <ModalSlippageWarning
          handleSubmit={ignoreSlippageWarning}
          handleClose={() => setShowSlippageWarning(false)}
          isMobile={props.isMobile}
        />
      )}
    </>
  );
}

OrderFormMarket.propTypes = {
  funds: PropTypes.shape({ balances: PropTypes.array.isRequired }).isRequired,
  market: PropTypes.shape({
    minTickLength: PropTypes.number.isRequired,
    minQtyLength: PropTypes.number.isRequired,
    minQty: PropTypes.number.isRequired,
    baseSizeMinimum: PropTypes.number.isRequired,
    takerFee: PropTypes.number.isRequired,
    quoteCurrency: PropTypes.string.isRequired,
    baseCurrency: PropTypes.string.isRequired,
    quoteIncrement: PropTypes.number.isRequired,
    quoteAsset: PropTypes.string.isRequired
  }),
  orderBook: PropTypes.object.isRequired,
  trades: PropTypes.object.isRequired,
  pair: PropTypes.string.isRequired,
  userId: PropTypes.string,
  side: PropTypes.oneOf(['buy', 'sell']).isRequired,
  onSuccess: PropTypes.func.isRequired,
  selectedOrder: PropTypes.shape({
    price: PropTypes.string,
    qty: PropTypes.string
  }),
  isMobile: PropTypes.bool.isRequired,
  takerFee: PropTypes.string.isRequired
};

OrderFormMarket.defaultProps = {
  market: null,
  userId: null,
  selectedOrder: { price: null, qty: null }
};

export default OrderFormMarket;
